import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import { useNavigate } from 'react-router-dom';
import {
  FaRocket,
  FaWallet,
  FaCheckCircle,
  FaTimesCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaFire,
  FaTag,
  FaUserShield,
  FaLock,
  FaUnlock,
  FaCalendarAlt,
  FaExclamationTriangle
} from 'react-icons/fa';
import FeaturedITOs from '../components/FeaturedITOs';
import '../styles/MintPage.css';

const MintPage = () => {
  const { isConnected, walletAddress, connectWallet } = useWallet();
  const [displayItos, setDisplayItos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIto, setSelectedIto] = useState(null);
  const [selectedPackIndex, setSelectedPackIndex] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [userBalance, setUserBalance] = useState(0);
  const [transactionStatus, setTransactionStatus] = useState('');
  const [transactionState, setTransactionState] = useState(''); // 'processing', 'success', 'error'
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));

  const mintWindowRef = useRef(null);
  const navigate = useNavigate();

  // Update current time every second for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Helper: Format time remaining
  const formatTimeRemaining = (targetTime) => {
    const diff = targetTime - currentTime;
    if (diff <= 0) return null;

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  // Helper: Check if user is whitelisted
  const isUserWhitelisted = (ito) => {
    if (!walletAddress || !ito.whitelistInfo) return false;
    return ito.whitelistInfo.some(w => w.address === walletAddress);
  };

  // Helper: Get user's whitelist limit
  const getUserWhitelistLimit = (ito) => {
    if (!walletAddress || !ito.whitelistInfo) return 0;
    const entry = ito.whitelistInfo.find(w => w.address === walletAddress);
    return entry ? entry.limit : 0;
  };

  // Helper: Check ITO phase (whitelist, public, ended, not started)
  const getItoPhase = (ito) => {
    const now = currentTime;

    // Check if ITO has ended
    if (ito.endTime && ito.endTime > 0 && now > ito.endTime) {
      return { phase: 'ended', message: 'ITO has ended' };
    }

    // Check if ITO hasn't started
    if (ito.startTime && ito.startTime > 0 && now < ito.startTime) {
      return {
        phase: 'not_started',
        message: 'ITO starts in',
        countdown: formatTimeRemaining(ito.startTime)
      };
    }

    // Check whitelist phase
    if (ito.whitelistStatus === 1) {
      const wlStart = ito.whitelistStartTime || 0;
      const wlEnd = ito.whitelistEndTime || 0;

      if (wlStart > 0 && now < wlStart) {
        return {
          phase: 'whitelist_pending',
          message: 'Whitelist starts in',
          countdown: formatTimeRemaining(wlStart)
        };
      }

      if ((wlStart === 0 || now >= wlStart) && (wlEnd === 0 || now < wlEnd)) {
        return {
          phase: 'whitelist',
          message: wlEnd > 0 ? 'Whitelist ends in' : 'Whitelist active',
          countdown: wlEnd > 0 ? formatTimeRemaining(wlEnd) : null
        };
      }
    }

    // Public phase
    if (ito.endTime && ito.endTime > 0) {
      return {
        phase: 'public',
        message: 'Ends in',
        countdown: formatTimeRemaining(ito.endTime)
      };
    }

    return { phase: 'public', message: 'Public mint' };
  };

  // Helper: Check if user can mint
  const canUserMint = (ito) => {
    const phase = getItoPhase(ito);

    if (phase.phase === 'ended' || phase.phase === 'not_started') {
      return { canMint: false, reason: phase.message };
    }

    if (phase.phase === 'whitelist_pending') {
      return { canMint: false, reason: 'Whitelist not started yet' };
    }

    if (phase.phase === 'whitelist') {
      if (!isUserWhitelisted(ito)) {
        return { canMint: false, reason: 'Not whitelisted' };
      }
    }

    return { canMint: true, reason: null };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const itoResponse = await axios.get('https://api.mainnet.klever.org/v1.0/ito/list?page=1&limit=100');
        const itos = itoResponse.data.data.itos;

        const filteredItos = itos.filter(ito => 
          ito.isActive && ito.mintedAmount < ito.maxAmount
        );

        if (filteredItos.length === 0) {
          setDisplayItos([]);
          setLoading(false);
          return;
        }

        const assetIds = filteredItos.map(ito => ito.assetId);
        const keys = filteredItos.flatMap(ito => ito.packData.map(pack => pack.key));

        const allAssets = await fetchAssetsByIdsAndKeys(assetIds, keys);

        const matchedItos = filteredItos.map(ito => {
          const asset = allAssets.find(asset => asset.assetId === ito.assetId && asset.assetType === "NonFungible");
          const keyAsset = allAssets.find(asset => asset.assetId === ito.packData[0].key);

          const precision = keyAsset ? keyAsset.precision : 0;

          if (asset) {
            const updatedLogoUrl = replaceLogoUri(asset.assetId, asset.logo);
            return {
              ...ito,
              logoUrl: updatedLogoUrl,
              name: asset.name,
              precision,
              pricePerPack: ito.packData[0].packs[0].price,
              key: ito.packData[0].key,
              // Include whitelist and timing data
              startTime: ito.startTime || 0,
              endTime: ito.endTime || 0,
              whitelistStatus: ito.whitelistStatus || 0,
              whitelistStartTime: ito.whitelistStartTime || 0,
              whitelistEndTime: ito.whitelistEndTime || 0,
              whitelistInfo: ito.whitelistInfo || [],
              defaultLimitPerAddress: ito.defaultLimitPerAddress || 0
            };
          }
          return null;
        }).filter(ito => ito !== null);

        setDisplayItos(matchedItos);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    const fetchAssetsByIdsAndKeys = async (assetIds, keys) => {
      let page = 1;
      let fetchedAssets = [];
      let response;

      do {
        response = await axios.get(`https://api.mainnet.klever.org/v1.0/assets/list?page=${page}&limit=100`);
        const assets = response.data.data.assets;

        const matchingAssets = assets.filter(asset => assetIds.includes(asset.assetId) || keys.includes(asset.assetId));
        fetchedAssets = fetchedAssets.concat(matchingAssets);
        page += 1;
      } while (response.data.data.assets.length === 100);

      return fetchedAssets;
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      if (isConnected && selectedIto) {
        try {
          const response = await axios.get(`https://api.mainnet.klever.org/v1.0/address/${walletAddress}`);
          const assets = response.data.data.account.assets || {};
          const assetBalance = assets[selectedIto.key]?.balance || 0;
          setUserBalance(assetBalance / Math.pow(10, selectedIto.precision));
        } catch (error) {
          console.error('Error fetching balance:', error);
          setUserBalance(0);
        }
      }
    };

    fetchBalance();
  }, [isConnected, selectedIto, walletAddress]);

  const replaceLogoUri = (assetId, logoUrl) => {
    if (assetId === 'WCNFT-25HY') return null;
    if (!logoUrl) return logoUrl;
    if (logoUrl.includes("https://klever-mint.mypinata.cloud")) {
      return logoUrl.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
    }
    return logoUrl;
  };

  const formatPrice = (price, precision) => {
    if (precision === 0) return price.toString();
    return parseFloat(price.toFixed(precision)).toString();
  };

  const handleLogoError = (e) => {
    e.target.style.display = 'none';
    const placeholder = e.target.nextSibling;
    if (placeholder) placeholder.style.display = 'flex';
  };

  const handleMintClick = (ito) => {
    setSelectedIto(ito);
    setSelectedPackIndex(0);
    const firstPack = ito.packData[0].packs[0];
    setTotalPrice(calculateTotalPrice(firstPack, ito.precision));
    setTransactionStatus('');
    setTransactionState('');
    document.body.classList.add('modal-open');
    document.addEventListener('mousedown', handleClickOutside);
  };

  const calculateTotalPrice = (pack, precision) => {
    const totalPrice = (pack.amount * pack.price) / Math.pow(10, precision);
    return formatPrice(totalPrice, precision);
  };

  const closeMintWindow = () => {
    setSelectedIto(null);
    setSelectedPackIndex(0);
    setTotalPrice(0);
    setTransactionStatus('');
    setTransactionState('');
    document.body.classList.remove('modal-open');
    document.removeEventListener('mousedown', handleClickOutside);
  };

  const mintNft = useCallback(async () => {
    setTransactionState('processing');
    setTransactionStatus('Processing transaction...');

    const payload = {
      buyType: 0,
      id: selectedIto.assetId,
      amount: selectedIto.packData[0].packs[selectedPackIndex].amount,
      currencyId: selectedIto.packData[0].key,
    };

    const contract = {
      type: 17,
      payload: payload
    };

    try {
      const unsignedTx = await web.buildTransaction([contract]);
      const signedTx = await web.signTransaction(unsignedTx);
      const response = await web.broadcastTransactions([signedTx]);

      console.log('Mint NFT transaction response:', response);
      setTransactionState('success');
      setTransactionStatus('Mint successful! Your NFTs are on the way!');
      setTimeout(() => {
        closeMintWindow();
        // Refresh the page to update minted amounts
        window.location.reload();
      }, 2500);
    } catch (error) {
      console.error('Error in mint NFT transaction:', error);
      setTransactionState('error');
      setTransactionStatus('Transaction failed. Please try again.');
      setTimeout(() => {
        setTransactionState('');
        setTransactionStatus('');
      }, 3000);
    }
  }, [selectedIto, selectedPackIndex]);

  const handleNextPack = () => {
    if (selectedIto && selectedPackIndex < selectedIto.packData[0].packs.length - 1) {
      const nextIndex = selectedPackIndex + 1;
      setSelectedPackIndex(nextIndex);
      setTotalPrice(calculateTotalPrice(selectedIto.packData[0].packs[nextIndex], selectedIto.precision));
    }
  };

  const handlePrevPack = () => {
    if (selectedIto && selectedPackIndex > 0) {
      const prevIndex = selectedPackIndex - 1;
      setSelectedPackIndex(prevIndex);
      setTotalPrice(calculateTotalPrice(selectedIto.packData[0].packs[prevIndex], selectedIto.precision));
    }
  };

  const handleClickOutside = (event) => {
    if (mintWindowRef.current && !mintWindowRef.current.contains(event.target)) {
      if (transactionState !== 'processing') {
        closeMintWindow();
      }
    }
  };

  const getMintProgress = (ito) => {
    return (ito.mintedAmount / ito.maxAmount) * 100;
  };

  if (loading) {
    return (
      <div className="mint-page">
        <div className="mint-loading">
          <div className="loading-spinner-large"></div>
          <p className="loading-text">Loading active mints...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mint-page">
      {/* Hero Section */}
      <div className="mint-hero">
        <div className="mint-hero-content">
          <h1 className="mint-hero-title">
            <FaRocket className="hero-icon" />
            Mint NFTs
          </h1>
          <p className="mint-hero-subtitle">
            Discover and mint exclusive NFT collections on KleverChain
          </p>
        </div>
      </div>

      {/* Featured ITOs Section */}
      <FeaturedITOs />

      {/* Stats Bar */}
      {displayItos.length > 0 && (
        <div className="mint-stats-bar">
          <div className="stat-item">
            <FaFire className="stat-icon" />
            <div className="stat-content">
              <span className="stat-label">Active Mints</span>
              <span className="stat-value">{displayItos.length}</span>
            </div>
          </div>
          <div className="stat-item">
            <FaTag className="stat-icon" />
            <div className="stat-content">
              <span className="stat-label">Total Collections</span>
              <span className="stat-value">{displayItos.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Mint Grid */}
      <div className="mint-container">
        {displayItos.length > 0 ? (
          <div className="mint-grid">
            {displayItos.map((ito, index) => {
              const { logoUrl, name, precision } = ito;
              const pack = ito.packData[0];
              const price = precision ? pack.packs[0].price / Math.pow(10, precision) : pack.packs[0].price;
              const progress = getMintProgress(ito);
              const phase = getItoPhase(ito);
              const userWhitelisted = isUserWhitelisted(ito);

              return (
                <div
                  key={ito.assetId}
                  className="mint-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mint-card-header">
                    <div className="mint-card-image-wrapper">
                      {logoUrl ? (
                        <>
                          <img
                            src={logoUrl}
                            alt={name}
                            className="mint-card-image"
                            onError={handleLogoError}
                          />
                          <div className="mint-image-placeholder" style={{ display: 'none' }}>
                            <FaTag size={40} />
                          </div>
                        </>
                      ) : (
                        <div className="mint-image-placeholder">
                          <FaTag size={40} />
                        </div>
                      )}
                    </div>
                    {/* Phase Badge */}
                    {phase.phase === 'whitelist' && (
                      <div className="mint-phase-badge whitelist">
                        <FaUserShield /> Whitelist
                      </div>
                    )}
                    {phase.phase === 'not_started' && (
                      <div className="mint-phase-badge upcoming">
                        <FaClock /> Coming Soon
                      </div>
                    )}
                  </div>

                  <div className="mint-card-body">
                    <h3 className="mint-card-name">{name || 'Unnamed Collection'}</h3>

                    {/* Timer Display */}
                    {phase.countdown && (
                      <div className={`mint-timer ${phase.phase}`}>
                        <FaClock />
                        <span>{phase.message}: {phase.countdown}</span>
                      </div>
                    )}

                    {/* Whitelist Status for connected user */}
                    {isConnected && ito.whitelistStatus === 1 && phase.phase === 'whitelist' && (
                      <div className={`whitelist-status ${userWhitelisted ? 'whitelisted' : 'not-whitelisted'}`}>
                        {userWhitelisted ? (
                          <>
                            <FaCheckCircle /> You're whitelisted
                          </>
                        ) : (
                          <>
                            <FaLock /> Not whitelisted
                          </>
                        )}
                      </div>
                    )}

                    <div className="mint-progress-section">
                      <div className="progress-labels">
                        <span className="progress-label">Minted</span>
                        <span className="progress-numbers">
                          {ito.mintedAmount.toLocaleString()} / {ito.maxAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="mint-price-section">
                      <div className="price-label">Starting at</div>
                      <div className="price-display">
                        <span className="price-amount">{formatPrice(price, precision)}</span>
                        <span className="price-currency">{pack.key}</span>
                      </div>
                    </div>

                    <button
                      className="mint-card-button"
                      onClick={() => handleMintClick(ito)}
                      disabled={phase.phase === 'ended' || phase.phase === 'not_started'}
                    >
                      {phase.phase === 'ended' ? (
                        <><FaTimesCircle /> Ended</>
                      ) : phase.phase === 'not_started' ? (
                        <><FaClock /> Coming Soon</>
                      ) : (
                        <><FaRocket /> Mint Now</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-mints-message">
            <FaClock size={60} />
            <h2>No Active Mints</h2>
            <p>There are no active minting opportunities at the moment.</p>
            <p>Check back soon for new collections!</p>
          </div>
        )}
      </div>

      {/* Mint Modal */}
      {selectedIto && (
        <div className="mint-modal-overlay">
          <div className="mint-modal" ref={mintWindowRef}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedIto.name}</h2>
              <button 
                className="modal-close-button" 
                onClick={closeMintWindow}
                disabled={transactionState === 'processing'}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-image-section">
                {selectedIto.logoUrl ? (
                  <img 
                    src={selectedIto.logoUrl} 
                    alt={selectedIto.name} 
                    className="modal-image" 
                    onError={handleLogoError} 
                  />
                ) : (
                  <div className="modal-image-placeholder">
                    <FaTag size={60} />
                  </div>
                )}
              </div>

              <div className="modal-info-section">
                <div className="info-row">
                  <span className="info-label">Collection</span>
                  <span className="info-value">{selectedIto.assetId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Progress</span>
                  <span className="info-value">
                    {selectedIto.mintedAmount.toLocaleString()} / {selectedIto.maxAmount.toLocaleString()}
                  </span>
                </div>
                {selectedIto.defaultLimitPerAddress > 0 && (
                  <div className="info-row">
                    <span className="info-label">Limit/Address</span>
                    <span className="info-value">{selectedIto.defaultLimitPerAddress} NFTs</span>
                  </div>
                )}
              </div>

              {/* Whitelist & Timing Info */}
              {(() => {
                const phase = getItoPhase(selectedIto);
                const userWhitelisted = isUserWhitelisted(selectedIto);
                const mintCheck = canUserMint(selectedIto);

                return (
                  <>
                    {/* Phase Timer */}
                    {phase.countdown && (
                      <div className={`modal-timer ${phase.phase}`}>
                        <FaCalendarAlt />
                        <span>{phase.message}: <strong>{phase.countdown}</strong></span>
                      </div>
                    )}

                    {/* Whitelist Status Banner */}
                    {selectedIto.whitelistStatus === 1 && phase.phase === 'whitelist' && (
                      <div className={`modal-whitelist-banner ${userWhitelisted ? 'whitelisted' : 'not-whitelisted'}`}>
                        {userWhitelisted ? (
                          <>
                            <FaCheckCircle />
                            <div>
                              <strong>You're Whitelisted!</strong>
                              {getUserWhitelistLimit(selectedIto) > 0 && (
                                <span>Your limit: {getUserWhitelistLimit(selectedIto)} NFTs</span>
                              )}
                            </div>
                          </>
                        ) : isConnected ? (
                          <>
                            <FaLock />
                            <div>
                              <strong>Whitelist Only</strong>
                              <span>Your wallet is not on the whitelist</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <FaUserShield />
                            <div>
                              <strong>Whitelist Phase Active</strong>
                              <span>Connect wallet to check eligibility</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Warning if can't mint */}
                    {!mintCheck.canMint && mintCheck.reason && (
                      <div className="modal-mint-warning">
                        <FaExclamationTriangle />
                        <span>{mintCheck.reason}</span>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Pack Selector */}
              {selectedIto.packData[0].packs.length > 1 && (
                <div className="pack-selector-section">
                  <label className="section-label">Select Pack Size</label>
                  <div className="pack-selector">
                    <button
                      className="pack-nav-button"
                      onClick={handlePrevPack}
                      disabled={selectedPackIndex === 0 || transactionState === 'processing'}
                    >
                      <FaChevronLeft />
                    </button>
                    <div className="pack-display">
                      <div className="pack-amount">
                        {selectedIto.packData[0].packs[selectedPackIndex].amount}
                      </div>
                      <div className="pack-label">NFTs</div>
                    </div>
                    <button
                      className="pack-nav-button"
                      onClick={handleNextPack}
                      disabled={selectedPackIndex === selectedIto.packData[0].packs.length - 1 || transactionState === 'processing'}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              )}

              {/* Price & Balance */}
              <div className="price-balance-section">
                <div className="price-box">
                  <span className="box-label">Total Cost</span>
                  <div className="box-value-display">
                    <span className="box-amount">{totalPrice}</span>
                    <span className="box-currency">{selectedIto.packData[0].key}</span>
                  </div>
                </div>
                <div className="balance-box">
                  <span className="box-label">Your Balance</span>
                  <div className="box-value-display">
                    <span className="box-amount">{userBalance.toFixed(selectedIto.precision)}</span>
                    <span className="box-currency">{selectedIto.packData[0].key}</span>
                  </div>
                </div>
              </div>

              {/* Transaction Status */}
              {transactionStatus && (
                <div className={`transaction-status-box ${transactionState}`}>
                  {transactionState === 'processing' && (
                    <>
                      <div className="status-spinner"></div>
                      <span>{transactionStatus}</span>
                    </>
                  )}
                  {transactionState === 'success' && (
                    <>
                      <FaCheckCircle />
                      <span>{transactionStatus}</span>
                    </>
                  )}
                  {transactionState === 'error' && (
                    <>
                      <FaTimesCircle />
                      <span>{transactionStatus}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              {(() => {
                const mintCheck = canUserMint(selectedIto);
                const insufficientBalance = userBalance < parseFloat(totalPrice);
                const isDisabled = !mintCheck.canMint || (isConnected && insufficientBalance) || transactionState === 'processing';

                return (
                  <button
                    className="modal-action-button primary"
                    onClick={isConnected ? mintNft : connectWallet}
                    disabled={isDisabled}
                  >
                    {transactionState === 'processing' ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>Processing...</span>
                      </>
                    ) : !isConnected ? (
                      <>
                        <FaWallet />
                        <span>Connect Wallet</span>
                      </>
                    ) : !mintCheck.canMint ? (
                      <>
                        <FaLock />
                        <span>{mintCheck.reason}</span>
                      </>
                    ) : insufficientBalance ? (
                      <>
                        <FaTimesCircle />
                        <span>Insufficient Balance</span>
                      </>
                    ) : (
                      <>
                        <FaRocket />
                        <span>Mint Now</span>
                      </>
                    )}
                  </button>
                );
              })()}
              <button
                className="modal-action-button secondary"
                onClick={closeMintWindow}
                disabled={transactionState === 'processing'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MintPage;